import React from "react";
import {useIntl} from "react-intl";
import {Link} from "react-router-dom";

import {calcAvailableForTerminal, getPayoutFee} from "../../Services/feeCalculator";
import classes from "./styles.module.css";

/**
 * Информеры под выбором проекта и терминала на страницах реестров.
 *
 * variant="project"  — общий остаток по терминалам проекта.
 * variant="terminal" — сколько реально можно выплатить через выбранный
 *                      терминал с учётом комиссии.
 *
 * Отдельно живут подсказки про соседний раздел: у карточных реестров и реестров
 * СБП разные требования к файлу, и мерчант регулярно приходит не туда.
 */
function RegistryInfoCard(props) {
    const intl = useIntl();

    if (props.variant === "project") {
        return renderProjectCard(props, intl);
    }

    if (props.variant === "terminal") {
        return renderTerminalCard(props, intl);
    }

    return null;
}

/**
 * Пустое состояние: у проекта нет ни одного терминала под нужный способ
 * выплаты. Раньше такого сообщения не было, потому что список терминалов не
 * фильтровался вовсе. Теперь неподходящие скрыты, и без объяснения человек
 * упирался бы в пустой выпадающий список.
 */
export function NoTerminalsForMethod(props) {
    const intl = useIntl();
    const currentMethod = props.currentMethod;

    if (currentMethod !== "card" && currentMethod !== "sbp") {
        return null;
    }

    const text =
        currentMethod === "card"
            ? intl.formatMessage({
                  id: "RegistryInfoCard.noTerminals.card",
                  defaultMessage: "У этого проекта нет терминалов для выплат на банковские карты.",
              })
            : intl.formatMessage({
                  id: "RegistryInfoCard.noTerminals.sbp",
                  defaultMessage: "У этого проекта нет терминалов для выплат через СБП.",
              });

    return (
        <div className={classes.Card}>
            <div className={classes.Title}>{text}</div>
            <RegistryCrossLink currentMethod={currentMethod}/>
        </div>
    );
}

/**
 * Кнопка перехода в соседний раздел.
 */
export function RegistryCrossLink(props) {
    const intl = useIntl();
    const currentMethod = props.currentMethod;

    let to;
    let label;
    let hint;

    if (currentMethod === "card") {
        to = "/mobile-registry-upload";
        label = intl.formatMessage({
            id: "RegistryInfoCard.crossLink.sbpLabel",
            defaultMessage: "Перейти к реестру СБП →",
        });
        hint = intl.formatMessage({
            id: "RegistryInfoCard.crossLink.sbpHint",
            defaultMessage: "Нужно выплатить по номерам телефонов через СБП?",
        });
    } else if (currentMethod === "sbp") {
        to = "/registry-upload";
        label = intl.formatMessage({
            id: "RegistryInfoCard.crossLink.cardLabel",
            defaultMessage: "Перейти к карточному реестру →",
        });
        hint = intl.formatMessage({
            id: "RegistryInfoCard.crossLink.cardHint",
            defaultMessage: "Нужно выплатить на банковские карты?",
        });
    } else {
        return null;
    }

    return (
        <div className={classes.CrossLinkBlock}>
            <span className={classes.CrossLinkHint}>{hint}</span>
            <Link to={to} className={classes.CrossLink}>
                {label}
            </Link>
        </div>
    );
}

/**
 * «Похоже, это реестр из другого раздела».
 *
 * Текст подсказки приходит с бэкенда вместе с ошибкой формата: там же лежит
 * разбор шапки файла, и держать второе такое знание в кабинете значило бы
 * разойтись с загрузчиком в первый же день.
 */
export function WrongRegistryTypeHint(props) {
    const intl = useIntl();
    const wrongType = props.wrongType;

    if (wrongType !== "card" && wrongType !== "sbp") {
        return null;
    }

    const to = wrongType === "sbp" ? "/mobile-registry-upload" : "/registry-upload";
    const label =
        wrongType === "sbp"
            ? intl.formatMessage({
                  id: "RegistryInfoCard.crossLink.sbpLabel",
                  defaultMessage: "Перейти к реестру СБП →",
              })
            : intl.formatMessage({
                  id: "RegistryInfoCard.crossLink.cardLabel",
                  defaultMessage: "Перейти к карточному реестру →",
              });

    const text =
        props.hint ||
        (wrongType === "sbp"
            ? intl.formatMessage({
                  id: "RegistryInfoCard.wrongType.sbp",
                  defaultMessage: "Похоже, это реестр СБП: в файле телефон и банк получателя.",
              })
            : intl.formatMessage({
                  id: "RegistryInfoCard.wrongType.card",
                  defaultMessage: "Похоже, это карточный реестр: в файле номера карт.",
              }));

    return (
        <div className={classes.WrongType}>
            <span className={classes.WrongTypeText}>{text}</span>
            <Link to={to} className={classes.CrossLink}>
                {label}
            </Link>
        </div>
    );
}

function renderProjectCard(props, intl) {
    const project = props.project;
    const terminals = Array.isArray(props.terminals) ? props.terminals : [];

    if (!project) {
        return null;
    }

    let totalBalance = 0;
    terminals.forEach(function (t) {
        const b = t && t.balance ? Number(t.balance.confirmed) : 0;

        if (isFinite(b)) {
            totalBalance += b;
        }
    });

    const label =
        project.displayName ||
        project.name ||
        intl.formatMessage({id: "RegistryInfoCard.projectFallback", defaultMessage: "Проект"});

    const currency = terminals.length > 0 && terminals[0].currency ? terminals[0].currency : "RUB";

    return (
        <div className={classes.Card}>
            <div className={classes.Title}>
                {intl.formatMessage({id: "RegistryInfoCard.projectBalance", defaultMessage: "Баланс проекта"})}
            </div>
            <div className={classes.Row}>
                <span className={classes.RowLabel}>{label}</span>
                <span className={classes.RowValue}>{formatAmount(totalBalance, currency, intl.locale)}</span>
            </div>
        </div>
    );
}

function renderTerminalCard(props, intl) {
    const terminal = props.terminal;

    if (!terminal) {
        return null;
    }

    let balance = terminal.balance ? Number(terminal.balance.confirmed) : 0;
    if (!isFinite(balance)) {
        balance = 0;
    }

    const fee = getPayoutFee(terminal);
    const available = calcAvailableForTerminal(terminal, balance);
    const cost = fee && fee.cost != null ? Number(fee.cost) : 0;
    const min = fee && fee.costFixedMinimum != null ? Number(fee.costFixedMinimum) : 0;
    const currency = terminal.currency || "RUB";

    return (
        <React.Fragment>
            <div className={classes.Card}>
                <div className={classes.Title}>
                    {intl.formatMessage({
                        id: "RegistryInfoCard.availableForPayouts",
                        defaultMessage: "Доступно к выплатам",
                    })}
                </div>
                <div className={classes.MethodBody}>
                    <div className={classes.MethodAmount}>{formatAmount(available, currency, intl.locale)}</div>
                    <div className={classes.MethodFee}>
                        {intl.formatMessage(
                            {
                                id: "RegistryInfoCard.fee",
                                defaultMessage: "(комиссия {percent}, минимум {minimum})",
                            },
                            {
                                percent: formatPct(cost),
                                minimum: formatAmount(min, currency, intl.locale),
                            }
                        )}
                    </div>
                </div>
            </div>
            <div className={classes.Disclaimer}>
                {intl.formatMessage({
                    id: "RegistryInfoCard.disclaimer",
                    defaultMessage:
                        "Сумма, доступная к выплатам, расчётная и может отличаться, если применяется минимальная комиссия. Точная сумма считается при загрузке реестра.",
                })}
            </div>
        </React.Fragment>
    );
}

function formatAmount(value, currency, locale) {
    const n = Number(value) || 0;

    try {
        return new Intl.NumberFormat(locale || "ru", {
            style: "currency",
            currency: currency || "RUB",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(n);
    } catch (e) {
        // Незнакомый код валюты Intl роняет исключением — цифру всё равно
        // показываем, без неё карточка бессмысленна.
        return n.toFixed(2) + " " + (currency || "");
    }
}

function formatPct(value) {
    const n = Number(value) || 0;

    return (Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2)) + "%";
}

export default RegistryInfoCard;



// WEBPACK FOOTER //
// ./src/Components/RegistryInfoCard/index.js